import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow, { ActionStatus } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import StatusIcon from '../StatusIcon';

import SyncPatientModal from './SyncPatientModal';

interface Props {
  action: SyncPatientToEhrAction;
  workflow: Workflow;
  // The FIRST send. Valid only while the action is `pending`/`action_required` — the API's
  // DISPATCHABLE_FROM.
  onDispatch: (actionId: string, outputs?: WorkflowValuesInput) => unknown;
  // Every send AFTER an attempt has finished — a retry, or a push to a newly selected facility. It
  // reruns first, so the vendor call rides a fresh instance (design 010). Two calls rather than one
  // because `errored` and `completed` are deliberately not dispatchable: one attempt per instance is
  // what lets the engine drop a late result from a superseded attempt.
  onResend: (action: SyncPatientToEhrAction, outputs?: WorkflowValuesInput) => unknown;
  isDispatching: boolean;
  // Persist the corrected demographics on the referral before sending. Values, not staged action
  // outputs: a corrected name is a fact about the patient and should survive this send (and outlive a
  // retry, which re-reads them). MANUAL provenance outranks the extraction's.
  onUpdateValues: (values: WorkflowValuesInput) => Promise<unknown>;
}

const PLATFORM_LABELS: Record<string, string> = {
  matrixcare: 'MatrixCare',
  pcc: 'PointClickCare',
};

export function platformLabel(platform: string | null): string {
  if (!platform) return 'the EHR';
  return PLATFORM_LABELS[platform] ?? platform;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Note = styled(BodySmall)<{ $tone?: 'warn' | 'error' }>`
  color: ${({ $tone }) => ($tone === 'error' ? colors.red100 : $tone === 'warn' ? colors.orangeHover : colors.black50)};
`;

// The vendor's verbatim sentence. Quoted and kept whole — paraphrasing it would strip the only
// actionable detail a coordinator gets ("MedicalRecordNumber is required for this facility").
const VendorMessage = styled(BodySmall)`
  color: ${colors.black100};
  white-space: pre-wrap;
`;

const Detail = styled(BodySmall)`
  color: ${colors.black50};
`;

/**
 * The Sync Patient to EHR card body (OLIO-2846).
 *
 * EVERY state below is derived from `action` + `workflow` — there is no useState here beyond the
 * modal's openness, and that is deliberate rather than incidental. The only thing that moves this
 * card is the server: a settle pass, or a refetch after the platform's callback. Local state would
 * survive a refetch and start disagreeing with it, and a half-sent card that says the wrong thing
 * about a real patient in a real EHR is the worst outcome available here.
 *
 * The referral is NOT terminal while this renders (design 010) — recording the outcome ends the
 * competition but the run completes by exhaustion, which this action is the last thing standing in
 * the way of. So the run is live, polling continues, and `rerun` works, which is what makes the
 * retry path a fresh instance rather than a second dispatch of the same one.
 *
 * Presentation reads `effectiveStatus`, behavior reads `status` — the ClinicalReviewPanel rule. The
 * two genuinely differ for this kind: duplicate/locked/matched/no_match come back `completed` and are
 * presented as `flagged`, so keying behavior off the presentation status would offer a Retry on a
 * push that actually succeeded.
 */
export default function SyncPatientBody({
  action,
  workflow,
  onDispatch,
  onResend,
  isDispatching,
  onUpdateValues,
}: Props) {
  // The ONE piece of local state in this component, and only because a modal's openness is not a fact
  // about the referral. `reopenOnReview` distinguishes a fresh send (start at the document step) from
  // fixing a vendor rejection (go straight to the field it complained about).
  const [modal, setModal] = useState<null | 'fresh' | 'fix'>(null);
  const platform = platformLabel(workflow.ehrSync.platform);
  const selectedFacilityId = workflow.valueFor('selected_facility_id')?.value ?? null;
  const facilityName = (id: string | null) => workflow.groups.find((group) => group.id === id)?.name ?? 'the facility';

  // AC 7 by CONTENT, not status — mirroring the API's guard. A landed sync whose recorded facility is
  // no longer the referral's winner is STALE, and the coordinator must be able to push to the new
  // building. Deriving it (rather than tracking a flag) is what makes the button come back on its own
  // when the winner changes: the value write invalidates the query, the card re-renders, done.
  //
  // `isSyncedSuccessfully`, NOT `isCompleted`. duplicate/locked/matched/no_match also arrive completed
  // and carry the facility they were ATTEMPTED against, so completion would read a refusal as a landed
  // record — and this branch's copy tells the coordinator a real patient is in that building and must
  // be discharged there by hand. A duplicate creates no resident at all, so that is a false statement
  // sending someone after a record nobody made. The API learned this same lesson as
  // SUCCESS_CLASSIFICATIONS; the FE must not re-derive it from status.
  // Read across the SIBLINGS, because the fact and the work now live on different instances. The sync
  // is per-building: after a push lands at A and the coordinator moves the winner to B, A's instance
  // holds the landed result and B's is the one that must be sent — and B is the instance this card
  // renders. Reading only `action` here would drop the warning entirely in exactly that case, which
  // is the case it exists for.
  //
  // Self is included so a run created before the per_group reshape — one shared, ungrouped instance —
  // still behaves. Publishing does not back-fill, so those runs exist.
  const landedElsewhere = workflow.actions
    .filter((sibling): sibling is SyncPatientToEhrAction => sibling.kind === action.kind)
    .find(
      (sibling) =>
        sibling.isSyncedSuccessfully &&
        !!sibling.syncedFacilityGroupId &&
        !!selectedFacilityId &&
        sibling.syncedFacilityGroupId !== selectedFacilityId
    );

  // This instance itself landed somewhere that is no longer the winner — the pre-reshape shape, where
  // one shared row carried both the result and the next push.
  const syncedElsewhere = landedElsewhere?.id === action.id;

  // Every path to a vendor write goes through the review modal — there is deliberately no direct
  // dispatch here. A push creates a real record in a real facility, and the demographics were
  // extracted by an AI, so a person confirms them at least once before the first attempt and again
  // before every retry (the packet or the data may have changed since the failure).
  const sendButton = (label: string, mode: 'fresh' | 'fix' = 'fresh') => (
    <Button
      variant='primary'
      size='small'
      disabled={isDispatching}
      onClick={() => setModal(mode)}
      data-testid='sync-patient-send'>
      {isDispatching ? 'Sending…' : label}
    </Button>
  );

  // Which call the modal's Send makes is a question about the ROW, not about which button opened the
  // modal: an instance that has already finished (errored, or completed against a facility that is no
  // longer selected) is not dispatchable, and every other state is. Deriving it here rather than
  // passing a flag down each branch is what stops a future branch from picking the wrong one.
  const needsFreshInstance = action.isErrored || action.isCompleted;

  const reviewModal = modal && (
    <SyncPatientModal
      workflow={workflow}
      startOnReview={modal === 'fix'}
      onClose={() => setModal(null)}
      onSend={async (values, outputs) => {
        // Ordered, not atomic: values land first so the send always reads the corrected data. The
        // reverse would push stale demographics. If the send then fails, the correction is still
        // saved and the coordinator retries — benign.
        if (Object.keys(values).length > 0) await onUpdateValues(values);
        return needsFreshInstance ? onResend(action, outputs) : onDispatch(action.id, outputs);
      }}
    />
  );

  // Quiet until the referral is decided. `pending` means the required inputs — the outcome and the
  // selected facility — are not there yet, so there is nothing to send and nobody owes a click; the
  // card renders its definition description instead (actionConfig drops `bodyWhenPending` for exactly
  // this). `action_required` is the ready-to-send state, not `pending`.
  if (action.isPending) return null;

  if (action.isRunning) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <Row>
          <StatusIcon indicator='running' />
          <BodySmall>Sending to {platform}…</BodySmall>
        </Row>
      </Wrapper>
    );
  }

  // Errored splits on whether a push actually reached the vendor. With a message there is something
  // to fix and resend; without one we never got an answer, so all we can honestly offer is Retry.
  // Either way the send goes through `onResend` — `errored` is not dispatchable, so a retry is a new
  // instance. That is also why this branch survives the retry visually: the fresh instance renders as
  // ready-to-send rather than as a still-errored row, which is the honest reading of what happened.
  if (action.isErrored) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <Note $tone='error' data-testid='sync-patient-error'>
          Failed to send to {platform}.
        </Note>
        {action.vendorMessage && <VendorMessage>{action.vendorMessage}</VendorMessage>}
        {sendButton('Retry')}
        {reviewModal}
      </Wrapper>
    );
  }

  // A vendor answer a person can act on: the platform reports it action_required *with* the message
  // preserved. This is the missing-MRN loop and any other fixable field rejection.
  if (action.isActionRequired && action.hasResult) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <Note $tone='warn'>{platform} could not accept this patient yet.</Note>
        {action.vendorMessage && (
          <VendorMessage data-testid='sync-patient-vendor-message'>{action.vendorMessage}</VendorMessage>
        )}
        {sendButton('Fix and resend', 'fix')}
        {reviewModal}
      </Wrapper>
    );
  }

  if (action.isCompleted && !syncedElsewhere) {
    // PCC returns only a PENDING record — the facility must accept it before a patient exists there.
    // Saying "Synced" would tell the coordinator the work is done when it is not (AC 8).
    if (action.isPendingAcceptance) {
      return (
        <Wrapper data-testid='sync-patient-body'>
          <Row>
            <StatusIcon indicator='complete' />
            <BodySmallBold>Queued for facility acceptance</BodySmallBold>
          </Row>
          <Detail>
            {facilityName(action.syncedFacilityGroupId)} must accept this patient in {platform} before the record
            exists.
          </Detail>
          {action.pendingPatientId && <Detail>Pending patient ID: {action.pendingPatientId}</Detail>}
        </Wrapper>
      );
    }

    // duplicate / locked / matched / no_match arrive completed and present as flagged. The vendor's
    // sentence IS the content here — there is nothing for us to add and nothing to act on in Olio.
    if (action.effectiveStatus === ActionStatus.FLAGGED) {
      return (
        <Wrapper data-testid='sync-patient-body'>
          <Note $tone='warn'>{platform} returned this patient for review.</Note>
          {action.vendorMessage && (
            <VendorMessage data-testid='sync-patient-vendor-message'>{action.vendorMessage}</VendorMessage>
          )}
        </Wrapper>
      );
    }

    return (
      <Wrapper data-testid='sync-patient-body'>
        <Row>
          <StatusIcon indicator='complete' />
          <BodySmallBold data-testid='sync-patient-synced'>Synced to {platform}</BodySmallBold>
        </Row>
        {action.residentId && <Detail>Resident ID: {action.residentId}</Detail>}
      </Wrapper>
    );
  }

  // The winner moved after a successful push. Both facts matter: the patient IS in the old building
  // (nobody can un-sync them — neither vendor has an API for it, so someone has to discharge or
  // reject that record by hand), and the new building has not been synced yet.
  if (syncedElsewhere) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <Note $tone='warn' data-testid='sync-patient-stale-facility'>
          Synced to {facilityName(action.syncedFacilityGroupId)}, which is no longer the selected facility.
        </Note>
        <Detail>
          The record in {facilityName(action.syncedFacilityGroupId)} is not removed by syncing again — it must be
          discharged or rejected there.
        </Detail>
        {sendButton(`Sync to ${facilityName(selectedFacilityId)}`)}
        {reviewModal}
      </Wrapper>
    );
  }

  // Ready to send: `action_required` with nothing recorded yet — the state the evaluator activates
  // this action into once the decision lands.
  //
  // GATED EXPLICITLY rather than left as the fall-through. The fall-through was a catch-all for every
  // status not handled above, and `flagged` reaches it — verified by rendering each status through
  // CategoryCard: flagged renders this body, where canceled and pending render none. A flagged sync
  // therefore offered a live Send button, and `flagged` is NOT in the API's DISPATCHABLE_FROM, so the
  // click could only 422 — which is precisely the self-blocking button the kind's own comment warns
  // about when it explains why the demographics are click-time checks rather than required inputs.
  // Naming the ready state instead of negating the others also means a new status can't silently
  // inherit a Send button.
  if (!action.isActionRequired) return null;

  // Hidden when the client has no patient-writable integration — the API refuses the dispatch, so a
  // button that always 422s is worse than nothing. The kind's `applicable?` normally parks such a row
  // `skipped` before this renders at all; this stays as the belt to that braces.
  if (!workflow.ehrSync.platform) return null;

  // The building has to carry a vendor facility id of its own (its External ID or its Location's).
  // Without one, DispatchGuard refuses at the click with "has no EHR facility identifier" — so say so
  // HERE, where the coordinator can act on it, instead of offering a button whose only possible
  // outcome is a 422. This is what `ehrSync.facilityIds` is served for.
  if (selectedFacilityId && !workflow.ehrSync.facilityIds.includes(selectedFacilityId)) {
    return (
      <Wrapper data-testid='sync-patient-body'>
        <Note $tone='warn' data-testid='sync-patient-no-facility-id'>
          {facilityName(selectedFacilityId)} has no {platform} facility identifier, so this patient cannot be sent there
          yet. Add the facility&apos;s External ID in settings, then send.
        </Note>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid='sync-patient-body'>
      {/* A push landed at a building that is no longer the winner, and this instance is the new
          winner's. Both facts matter and neither is obvious: the patient IS in the old building
          (nobody can un-sync them — neither vendor has an API for it), and the new one has not been
          sent yet. Pre-reshape both facts sat on one row; now they sit on two, which is why this
          reads siblings. */}
      {landedElsewhere && (
        <Note $tone='warn' data-testid='sync-patient-stale-facility'>
          Synced to {facilityName(landedElsewhere.syncedFacilityGroupId)}, which is no longer the selected facility.
          That record is not removed by syncing again — it must be discharged or rejected there.
        </Note>
      )}
      <BodySmall>
        Ready to send to {platform}
        {selectedFacilityId ? ` (${facilityName(selectedFacilityId)})` : ''}.
      </BodySmall>
      {sendButton(`Sync to ${platform}`)}
      {reviewModal}
    </Wrapper>
  );
}
